/* eslint-disable @typescript-eslint/restrict-plus-operands */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { HubConnection, HubConnectionBuilder, HubConnectionState, IRetryPolicy, RetryContext } from '@microsoft/signalr';
import { Observable, catchError, defer, lastValueFrom, tap, throwError } from 'rxjs';
import { Message, SignalRNegotiate, TransactionDTO, ConnectedDevice, AcknowledgeResponse } from 'src/app/models/signal-r.model';
import { SampleTransaction } from 'src/app/models/sample-transaction.model';
import { SharedService } from '../shared-service/shared.service';
import { CookieService } from '../cookieService/cookie.service';
import { CustomMessage, mapMessage } from 'src/app/models/enums/custom-message.enum';
import { CustomMessageType } from 'src/app/models/enums/custom-message-type.enum';
import { MonitoringService } from '../monitoring-service/monitoring.service';

/**
 * Perpetual reconnect policy: retries indefinitely so the payment pad self-heals after
 * extended outages (e.g. a SignalR regional failover), instead of giving up after the
 * SDK default of 4 attempts. Uses the same phased backoff shape as the AtlasCargo iOS app:
 * fast retries for the first 2 minutes, then progressively longer intervals, capped at 60s.
 */
class PerpetualRetryPolicy implements IRetryPolicy {
  private readonly phase1MaxAttempts = 10;
  private readonly phase1CapMs = 60000;
  private readonly phase2MaxAttempts = 30;
  private readonly phase2DelayMs = 30000;
  private readonly phase3DelayMs = 60000;

  constructor(private onRetryScheduled?: (retryContext: RetryContext, retryDelayMs: number) => void) {}

  nextRetryDelayInMilliseconds(retryContext: RetryContext): number {
    const attempt = retryContext.previousRetryCount + 1;
    let retryDelayMs: number;

    if (attempt <= this.phase1MaxAttempts) {
      const exponent = Math.min(attempt, 6);
      retryDelayMs = Math.min(Math.pow(2, exponent) * 1000, this.phase1CapMs);
      this.onRetryScheduled?.(retryContext, retryDelayMs);
      return retryDelayMs;
    }

    if (attempt <= this.phase2MaxAttempts) {
      retryDelayMs = this.phase2DelayMs;
      this.onRetryScheduled?.(retryContext, retryDelayMs);
      return retryDelayMs;
    }

    retryDelayMs = this.phase3DelayMs;
    this.onRetryScheduled?.(retryContext, retryDelayMs);
    return retryDelayMs;
  }
}

@Injectable()
export class SignalRService {
  signalRToken: SignalRNegotiate | undefined;
  isParameterValid = false;
  public connection: HubConnection;
  public connectedDevice: ConnectedDevice = {};
  public url = '';
  private apiBaseUrl = '/api/alaska';
  private addConnectedDeviceUrl = this.apiBaseUrl + '/addConnectedDevice';
  private sendTransactionUrl = this.apiBaseUrl + '/sendTransaction';
  private sendAcknowledgeMessageUrl = this.apiBaseUrl + '/sendAcknowledgeMessage';
  private negotiateUrl = this.apiBaseUrl + '/negotiate';

  private deviceId = '';
  private deviceType = 'PP';
  private transactionId = '';
  private pairedDevice: string | undefined;
  private ppDeviceCookieName = 'ppdid';
  private registrationGeneration = 0;
  private activePairingRegistrationGeneration: number | undefined;
  private activePairingDeviceId: string | undefined;
  private connectionPromise: Promise<void> | undefined;

  constructor(
    public http: HttpClient,
    private sharedService: SharedService,
    private cookieService: CookieService,
    private monitoringService: MonitoringService
  ) {
    this.connection = {} as HubConnection;
    this.sharedService.currentTransactionId.subscribe((transactionId) => (this.transactionId = transactionId));
    this.sharedService.currentParameterValid.subscribe((parameterValid) => (this.isParameterValid = parameterValid));
    this.sharedService.currentSelectedIpad.subscribe((ipadSelected) => (this.pairedDevice = ipadSelected?.deviceId));
  }

  public async connectToSignalR(): Promise<void> {
    if (this.connection?.state === HubConnectionState.Connected) {
      return;
    }

    if (!this.connectionPromise) {
      this.connectionPromise = this.connectWithRetry();
    }

    try {
      await this.connectionPromise;
    } finally {
      this.connectionPromise = undefined;
    }
  }

  private async connectWithRetry(): Promise<void> {
    let retryCount = 0;

    while (true) {
      try {
        this.deviceId = this.getDeviceId();
        console.log('CONNECTING TO SIGNALR');
        this.signalRToken = await lastValueFrom(this.getSignalRAccessToken());
        this.buildConnection(this.signalRToken);
        this.logTelemetry('CargoPaymentUI.SignalR.ConnectStarted', this.createTelemetryProperties());
        await this.connection.start();
        this.logTelemetry(
          'CargoPaymentUI.SignalR.ConnectCompleted',
          this.createTelemetryProperties({
            connectionId: this.connection.connectionId ?? undefined,
            hubState: this.getHubState(this.connection.state),
            outcome: 'connected',
          })
        );
        this.setSignalrClientMethods();
        return;
      } catch (error) {
        console.log('Error connectToSignalR()');
        this.logTelemetry(
          'CargoPaymentUI.SignalR.ConnectFailed',
          this.createTelemetryProperties({
            hubState: this.getHubState(this.connection?.state),
            httpStatus: this.getHttpStatus(error),
            failureCode: this.getFailureCode(error),
            outcome: 'failed',
          })
        );

        const retryDelayMs = Math.min(1000 * 2 ** Math.min(retryCount, 6), 60000);
        this.logTelemetry(
          'CargoPaymentUI.SignalR.RetryScheduled',
          this.createTelemetryProperties({
            hubState: this.getHubState(this.connection?.state),
            durationMs: retryDelayMs,
            failureCode: this.getFailureCode(error),
          })
        );
        retryCount += 1;
        await new Promise<void>((resolve) => setTimeout(resolve, retryDelayMs));
      }
    }
  }

  public getDeviceId(): string {
    if ((this.deviceId ?? '') == '') {
      if (!this.cookieService.checkCookie(this.ppDeviceCookieName)) {
        this.cookieService.setCookie(this.ppDeviceCookieName, this.sharedService.uuid(), 10000);
      }
    }
    return this.cookieService.getCookie(this.ppDeviceCookieName);
  }

  public buildConnection(token: SignalRNegotiate): void {
    this.connection = new HubConnectionBuilder()
      .withUrl(token.url, {
        accessTokenFactory: () => {
          return token.accessToken;
        },
      })
      .withAutomaticReconnect(
        new PerpetualRetryPolicy((retryContext, retryDelayMs) => {
          this.logTelemetry(
            'CargoPaymentUI.SignalR.RetryScheduled',
            this.createTelemetryProperties({
              hubState: 'Reconnecting',
              durationMs: retryDelayMs,
              failureCode: this.getFailureCode(retryContext.retryReason),
            })
          );
        })
      )
      .build();
  }

  public getSignalRAccessToken(): Observable<SignalRNegotiate> {
    return defer(() => {
      const startedAt = Date.now();
      this.logTelemetry('CargoPaymentUI.SignalR.NegotiateStarted', this.createTelemetryProperties());

      return this.http.post<SignalRNegotiate>(this.negotiateUrl, null).pipe(
        tap(() => {
          this.logTelemetry(
            'CargoPaymentUI.SignalR.NegotiateCompleted',
            this.createTelemetryProperties({
              durationMs: Date.now() - startedAt,
              outcome: 'success',
            })
          );
        }),
        catchError((error: unknown) => {
          this.logTelemetry(
            'CargoPaymentUI.SignalR.NegotiateFailed',
            this.createTelemetryProperties({
              durationMs: Date.now() - startedAt,
              httpStatus: this.getHttpStatus(error),
              failureCode: this.getFailureCode(error),
              outcome: 'failed',
            })
          );

          return throwError(() => error);
        })
      );
    });
  }

  setSignalrClientMethods(): void {
    if (this.connection && this.connection.connectionId) {
      this.connectedDevice = { deviceId: this.deviceId, deviceType: this.deviceType };
      console.log('Agent ConnectionId: ' + this.connection.connectionId);
      console.log('Agent DeviceId: ' + this.deviceId);
      console.log('Entering addConnectedDevice--ON CONNECTED-########');
      this.addConnectedDevice(this.connection.connectionId);
      this.connection.onreconnecting((error) => {
        this.logTelemetry(
          'CargoPaymentUI.SignalR.StateTransition',
          this.createTelemetryProperties({
            connectionId: this.connection.connectionId ?? undefined,
            hubState: this.getHubState(this.connection.state),
            failureCode: this.getFailureCode(error),
            outcome: 'reconnecting',
          })
        );
      });
      this.connection.onreconnected(() => {
        console.log('Entering addConnectedDevice--ON RECONECTED-**********');
        this.addConnectedDevice(this.connection.connectionId);
        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
        const message = 'Reconnected, New connectionId: ' + this.connection?.connectionId;
        console.log(message);
        this.logTelemetry(
          'CargoPaymentUI.SignalR.Reconnected',
          this.createTelemetryProperties({
            connectionId: this.connection.connectionId ?? undefined,
            hubState: this.getHubState(this.connection.state),
            outcome: 'reconnected',
          })
        );
      });

      this.connection.onclose((error) => {
        console.log('Disconnected');
        this.logTelemetry(
          'CargoPaymentUI.SignalR.Closed',
          this.createTelemetryProperties({
            connectionId: this.connection.connectionId ?? undefined,
            hubState: this.getHubState(this.connection.state),
            failureCode: this.getFailureCode(error),
            outcome: 'closed',
          })
        );
        // With a perpetual retry policy, onclose only fires when reconnection is no longer
        // possible (e.g. a fatal negotiate failure) rather than after transient retries are
        // exhausted. Attempt a fresh negotiate/connect so the pad can self-heal instead of
        // sitting disconnected until the agent manually refreshes the page.
        this.logTelemetry('CargoPaymentUI.SignalR.CloseReconnectStarted', this.createTelemetryProperties({ outcome: 'retrying' }));
        this.connectToSignalR().catch((reconnectError) => {
          console.log('Error reconnecting after close', reconnectError);
          this.logTelemetry(
            'CargoPaymentUI.SignalR.CloseReconnectFailed',
            this.createTelemetryProperties({
              httpStatus: this.getHttpStatus(reconnectError),
              failureCode: this.getFailureCode(reconnectError),
              outcome: 'failed',
            })
          );
        });
      });
      console.log('connected');
    }
  }

  public addConnectedDevice(connectionId: string | null): void {
    const registrationGeneration = ++this.registrationGeneration;
    const isExplicitPairingRegistration =
      this.activePairingRegistrationGeneration === registrationGeneration &&
      this.activePairingDeviceId === this.connectedDevice.pairedDeviceId;
    this.connectedDevice.deviceId = this.deviceId;
    this.connectedDevice.deviceConnectionId = connectionId != null ? connectionId : '';
    this.connectedDevice.deviceType = this.deviceType;
    this.connectedDevice.pairedDeviceId = this.pairedDevice;
    this.logTelemetry(
      'CargoPaymentUI.DeviceRegistration.Started',
      this.createTelemetryProperties({
        connectionId: connectionId ?? undefined,
        ipadDeviceId: this.connectedDevice.pairedDeviceId,
        registrationGeneration,
      })
    );

    this.http.post<TransactionDTO>(this.addConnectedDeviceUrl, this.connectedDevice).subscribe({
      next: () => {
        console.log('Device Added');
        this.logTelemetry(
          'CargoPaymentUI.DeviceRegistration.Completed',
          this.createTelemetryProperties({
            connectionId: connectionId ?? undefined,
            ipadDeviceId: this.connectedDevice.pairedDeviceId,
            registrationGeneration,
            outcome: 'success',
          })
        );
        if (isExplicitPairingRegistration) {
          this.logTelemetry(
            'CargoPaymentUI.Pairing.Completed',
            this.createTelemetryProperties({
              connectionId: connectionId ?? undefined,
              ipadDeviceId: this.connectedDevice.pairedDeviceId,
              registrationGeneration,
              outcome: 'success',
            })
          );
          this.clearActivePairing();
        }
        if (this.cookieService.checkCookie('pairedDevice')) {
          this.sharedService.updateContinueDeviceClick(true);
          mapMessage.set(CustomMessage.SuccessDevice, {
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            text: 'Device ' + this.connectedDevice.pairedDeviceId + ' successfully selected.',
            type: CustomMessageType.Success,
          });

          if (this.isParameterValid) this.sharedService.showMessage(mapMessage.get(CustomMessage.SuccessDevice));
        }
      },
      error: (error) => {
        this.logTelemetry(
          'CargoPaymentUI.DeviceRegistration.Failed',
          this.createTelemetryProperties({
            connectionId: connectionId ?? undefined,
            ipadDeviceId: this.connectedDevice.pairedDeviceId,
            registrationGeneration,
            httpStatus: this.getHttpStatus(error),
            failureCode: this.getFailureCode(error),
            outcome: 'failed',
          })
        );
        if (isExplicitPairingRegistration) {
          this.logTelemetry(
            'CargoPaymentUI.Pairing.Failed',
            this.createTelemetryProperties({
              connectionId: connectionId ?? undefined,
              ipadDeviceId: this.connectedDevice.pairedDeviceId,
              registrationGeneration,
              httpStatus: this.getHttpStatus(error),
              failureCode: this.getFailureCode(error),
              outcome: 'failed',
            })
          );
          this.clearActivePairing();
        }
        if (this.cookieService.checkCookie('pairedDevice')) {
          this.sharedService.updateContinueDeviceClick(false);
          this.sharedService.updateSelectedIpad(null);
        }
        console.log('Error adding device');
      },
    });
  }

  public pairWithConnectedDevice(pairedTo: string): void {
    this.pairedDevice = pairedTo;
    this.connectedDevice.pairedDeviceId = pairedTo;
    this.activePairingRegistrationGeneration = this.registrationGeneration + 1;
    this.activePairingDeviceId = pairedTo;
    this.addConnectedDevice(this.connection.connectionId);
  }

  public sendRequestToIpad(command: SampleTransaction): Observable<TransactionDTO> {
    command.transactionId ??= this.transactionId;
    const transaction: TransactionDTO = {
      to: this.pairedDevice || '',
      transaction: command,
      user: this.connectedDevice,
    };

    return defer(() => {
      const startedAt = Date.now();
      this.logTelemetry(
        'CargoPaymentUI.TransactionSend.Started',
        this.createTelemetryProperties({
          transactionId: command.transactionId,
          paymentAdviceNumber: command.paymentAdviceNumber,
          awb: command.awbNumber,
          ipadDeviceId: this.pairedDevice,
        })
      );

      return this.http.post<TransactionDTO>(this.sendTransactionUrl, transaction).pipe(
        tap(() => {
          this.logTelemetry(
            'CargoPaymentUI.TransactionSend.Completed',
            this.createTelemetryProperties({
              transactionId: command.transactionId,
              paymentAdviceNumber: command.paymentAdviceNumber,
              awb: command.awbNumber,
              ipadDeviceId: this.pairedDevice,
              outcome: 'success',
              durationMs: Date.now() - startedAt,
            })
          );
        }),
        catchError((error: unknown) => {
          this.logTelemetry(
            'CargoPaymentUI.TransactionSend.Failed',
            this.createTelemetryProperties({
              transactionId: command.transactionId,
              paymentAdviceNumber: command.paymentAdviceNumber,
              awb: command.awbNumber,
              ipadDeviceId: this.pairedDevice,
              httpStatus: this.getHttpStatus(error),
              failureCode: this.getFailureCode(error),
              outcome: 'failed',
              durationMs: Date.now() - startedAt,
            })
          );

          return throwError(() => error);
        })
      );
    });
  }

  public sendAcknowledgeMessage(): Observable<AcknowledgeResponse> {
    const messagge = {
      from: this.connectedDevice.deviceId,
      to: this.pairedDevice || '',
      transactionId: this.transactionId,
    };

    return this.http.post<AcknowledgeResponse>(this.sendAcknowledgeMessageUrl, messagge);
  }

  public sendMessage(message: Message): void {
    message.connectedDevice = this.connectedDevice;
    void this.connection.invoke('SendMessage', message);
  }

  private createTelemetryProperties(properties: { [key: string]: unknown } = {}): { [key: string]: unknown } {
    const telemetryProperties = {
      correlationId: this.transactionId || undefined,
      browserDeviceId: this.deviceId || this.cookieService.getCookie(this.ppDeviceCookieName) || undefined,
      ipadDeviceId: this.pairedDevice || undefined,
      browserConnectionId: this.connection?.connectionId ?? undefined,
      ipadConnectionId: undefined,
      connectionId: this.connection?.connectionId ?? undefined,
      hubState: this.getHubState(this.connection?.state),
      ...properties,
    };

    return Object.fromEntries(Object.entries(telemetryProperties).filter(([, value]) => value !== undefined && value !== null));
  }

  private logTelemetry(eventName: string, properties: { [key: string]: unknown }): void {
    this.monitoringService.logEvent(eventName, properties);
  }

  private clearActivePairing(): void {
    this.activePairingRegistrationGeneration = undefined;
    this.activePairingDeviceId = undefined;
  }

  private getHubState(state: HubConnectionState | string | undefined): string | undefined {
    if (state === undefined) {
      return undefined;
    }

    return typeof state === 'string' ? state : HubConnectionState[state] ?? String(state);
  }

  private getHttpStatus(error: unknown): number | undefined {
    if (error instanceof HttpErrorResponse) {
      return error.status;
    }

    if (typeof error === 'object' && error !== null && 'status' in error && typeof error.status === 'number') {
      return error.status;
    }

    return undefined;
  }

  private getFailureCode(error: unknown): string | undefined {
    if (error instanceof HttpErrorResponse) {
      const errorBody = error.error as { errorCode?: unknown; code?: unknown } | undefined;

      if (typeof errorBody?.errorCode === 'string') {
        return errorBody.errorCode;
      }

      if (typeof errorBody?.code === 'string') {
        return errorBody.code;
      }

      return error.statusText || String(error.status);
    }

    if (typeof error === 'object' && error !== null) {
      if ('error' in error && typeof error.error === 'object' && error.error !== null && 'errorCode' in error.error) {
        return typeof error.error.errorCode === 'string' ? error.error.errorCode : undefined;
      }

      if ('name' in error && typeof error.name === 'string') {
        return error.name;
      }
    }

    return error instanceof Error ? error.name : undefined;
  }
}
